<template>
  <div v-if="isVisible" class="debug-panel" :class="{ 'minimized': isMinimized }">
    <div class="debug-header" @click="toggleMinimize">
      <span class="debug-title">🐛 調試面板</span>
      <button @click.stop="clear" class="debug-clear">清空</button>
      <button @click.stop="close" class="debug-close">×</button>
    </div>
    <div v-if="!isMinimized" class="debug-content">
      <div class="debug-info">
        <div class="info-row">
          <span class="label">錢包狀態:</span>
          <span :class="['value', walletStore.isConnected ? 'success' : 'error']">
            {{ walletStore.isConnected ? '✅ 已連接' : '❌ 未連接' }}
          </span>
        </div>
        <div v-if="walletStore.isConnected" class="info-row">
          <span class="label">錢包地址:</span>
          <span class="value small">{{ walletStore.address?.slice(0, 10) }}...{{ walletStore.address?.slice(-8) }}</span>
        </div>
        <div class="info-row">
          <span class="label">網絡 ID:</span>
          <span class="value">{{ walletStore.chainId || 'N/A' }}</span>
        </div>
        <div class="info-row">
          <span class="label">連接類型:</span>
          <span class="value">{{ walletStore.connectionType || 'N/A' }}</span>
        </div>
        <div class="info-row">
          <span class="label">Provider:</span>
          <span class="value">{{ providerStatus }}</span>
        </div>
      </div>
      <div class="debug-logs">
        <div 
          v-for="(log, index) in logs" 
          :key="index" 
          :class="['log-item', log.type]"
        >
          <span class="log-time">{{ log.time }}</span>
          <span class="log-message">{{ log.message }}</span>
        </div>
      </div>
    </div>
  </div>
  <!-- 浮動按鈕 -->
  <button v-if="!isVisible" @click="show" class="debug-toggle">
    🐛
  </button>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useWalletStore } from '../stores/wallet'

const walletStore = useWalletStore()
const isVisible = ref(false)
const isMinimized = ref(false)
const logs = ref([])

// Provider 狀態檢測
const providerStatus = computed(() => {
  if (typeof window === 'undefined') return 'N/A'
  
  const checks = []
  if (window.okxwallet) checks.push('OKX Wallet ✅')
  if (window.ethereum) checks.push('window.ethereum ✅')
  if (walletStore.connector?.provider) checks.push('WalletConnect ✅')
  if (walletStore.appKit) checks.push('AppKit ✅')
  
  return checks.length > 0 ? checks.join(', ') : '❌ 無 Provider'
})

// 添加日誌
function addLog(message, type = 'info') {
  const time = new Date().toLocaleTimeString('zh-TW', { 
    hour12: false, 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit' 
  })
  
  logs.value.unshift({
    time,
    message,
    type
  })
  
  // 只保留最新 50 條
  if (logs.value.length > 50) {
    logs.value = logs.value.slice(0, 50)
  }
}

// 清空日誌
function clear() {
  logs.value = []
  addLog('日誌已清空', 'info')
}

// 顯示/隱藏
function show() {
  isVisible.value = true
  addLog('調試面板已打開', 'info')
}

function close() {
  isVisible.value = false
}

function toggleMinimize() {
  isMinimized.value = !isMinimized.value
}

// 監聽錢包狀態變化
watch(() => walletStore.isConnected, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    addLog(`錢包連接狀態變更: ${newVal ? '已連接' : '未連接'}`, newVal ? 'success' : 'error')
  }
})

watch(() => walletStore.address, (newVal) => {
  if (newVal) {
    addLog(`錢包地址: ${newVal.slice(0, 10)}...${newVal.slice(-8)}`, 'info')
  }
})

watch(() => walletStore.chainId, (newVal) => {
  if (newVal) {
    addLog(`網絡 ID: ${newVal}`, 'info')
  }
})

watch(() => walletStore.error, (newVal) => {
  if (newVal) {
    addLog(`錯誤: ${newVal}`, 'error')
  }
})

// 暴露給全局使用
window.debugLog = (message, type = 'info') => {
  addLog(message, type)
}

// 初始化
onMounted(() => {
  addLog('調試面板已初始化', 'info')
  
  // 檢測環境
  if (typeof window !== 'undefined') {
    addLog(`User Agent: ${navigator.userAgent.includes('Mobile') ? '手機' : '桌面'}`, 'info')
    
    // 檢測瀏覽器類型
    const isOKXBrowser = navigator.userAgent.includes('OKX') || window.okxwallet
    if (isOKXBrowser) {
      addLog(`瀏覽器: OKX 內置瀏覽器`, 'success')
    } else {
      addLog(`瀏覽器: 外部瀏覽器 (Safari/Chrome)`, 'info')
    }
    
    // 檢測 Provider
    if (window.okxwallet) {
      addLog(`window.okxwallet: 存在`, 'success')
    }
    if (window.ethereum) {
      addLog(`window.ethereum: 存在`, 'success')
    } else {
      addLog(`window.ethereum: 不存在`, 'warning')
    }
  }
  
  // 開發環境自動顯示
  if (import.meta.env.DEV) {
    isVisible.value = true
  }
})
</script>

<style scoped>
.debug-panel {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 90vw;
  max-width: 400px;
  max-height: 70vh;
  background: rgba(10, 15, 31, 0.98);
  border: 2px solid #3B82F6;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(59, 130, 246, 0.3);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  font-family: 'Courier New', monospace;
  backdrop-filter: blur(10px);
}

.debug-panel.minimized {
  max-height: 50px;
}

.debug-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  border-radius: 10px 10px 0 0;
  cursor: pointer;
  user-select: none;
}

.debug-title {
  font-size: 14px;
  font-weight: bold;
  color: white;
}

.debug-clear {
  padding: 4px 12px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.debug-clear:hover {
  background: rgba(255, 255, 255, 0.3);
}

.debug-close {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s;
}

.debug-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.debug-content {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
}

.debug-info {
  padding: 12px 16px;
  background: rgba(59, 130, 246, 0.1);
  border-bottom: 1px solid rgba(59, 130, 246, 0.3);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
}

.info-row:last-child {
  margin-bottom: 0;
}

.label {
  color: #9CA3AF;
  font-weight: 500;
}

.value {
  color: #E5E7EB;
  font-weight: bold;
}

.value.small {
  font-size: 11px;
}

.value.success {
  color: #10B981;
}

.value.error {
  color: #EF4444;
}

.debug-logs {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}

.log-item {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  font-size: 11px;
  line-height: 1.4;
  word-break: break-all;
}

.log-item.info {
  border-left: 3px solid #3B82F6;
}

.log-item.success {
  border-left: 3px solid #10B981;
}

.log-item.warning {
  border-left: 3px solid #F59E0B;
}

.log-item.error {
  border-left: 3px solid #EF4444;
}

.log-time {
  color: #6B7280;
  white-space: nowrap;
  font-size: 10px;
}

.log-message {
  color: #E5E7EB;
  flex: 1;
}

.debug-toggle {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  border: none;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
  z-index: 9999;
  transition: transform 0.2s;
}

.debug-toggle:active {
  transform: scale(0.95);
}

/* 移動端優化 */
@media (max-width: 768px) {
  .debug-panel {
    width: calc(100vw - 32px);
    max-width: none;
    bottom: 16px;
    right: 16px;
  }
  
  .debug-toggle {
    width: 48px;
    height: 48px;
    bottom: 16px;
    right: 16px;
    font-size: 20px;
  }
}
</style>

